/**
 * The HOA's own common-area tracts, straight from the county (2026-07-25).
 *
 * Maricopa codes HOA common area as PUC 0261 and names the association as the
 * owner. That answers the question that actually blocks a commercial bid —
 * "which ground does this client own?" — authoritatively, with real polygons.
 * Verified live: GREENWAY PARC THREE HOMEOWNERS ASSOCIATION owns APN 501-97-079,
 * 53,006 sq ft, inside plat GREENWAY PARC AT SURPRISE 3 AMD.
 *
 * This does not depend on OpenStreetMap at all.
 */
import { z } from 'zod';
import { fetchJson, ServiceError } from '../core/net';
import { polygonAreaSqFt, type LatLng } from '../core/geo';
import { sanitizeSubdivisionQuery, subdivisionTokens } from './subdivision';

const BASE = 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Parcels/MapServer/0/query';

/** Maricopa property-use code for common area owned by an association. */
export const PUC_COMMON_AREA = '0261';

export interface Tract {
  apn: string;
  owner: string;
  puc: string;
  countySqFt: number;      // the county's recorded LAND_SIZE
  measuredSqFt: number;    // our own geodesic measurement of the polygon
  subname?: string;
  rings: LatLng[][];
}

const Schema = z.object({
  features: z.array(z.object({
    attributes: z.object({
      APN: z.string().nullish(),
      OWNER_NAME: z.string().nullish(),
      PUC: z.string().nullish(),
      LAND_SIZE: z.number().nullish(),
      SUBNAME: z.string().nullish(),
    }),
    geometry: z.object({ rings: z.array(z.array(z.tuple([z.number(), z.number()]).rest(z.number()))) }).nullish(),
  })),
}).loose();

/** owner names that read as an association, for the second half of the filter */
const ASSOC = /(ASSOCIATION|ASSOC|HOMEOWNERS|COMMUNITY ASSN|\bHOA\b|MASTER ASSN)/i;

export interface TractQuery {
  /** plat name, e.g. "GREENWAY PARC AT SURPRISE 3 AMD" */
  subname?: string;
  /** fallback when there is no plat: the current map view [s,w,n,e] */
  bbox?: [number, number, number, number];
}

/**
 * Common-area tracts for a plat (preferred) or for the current map view.
 * Returns [] when the county has none — an answer, not an error.
 */
export async function findCommonAreaTracts(q: TractQuery): Promise<Tract[]> {
  if (q.subname) {
    // Exact first, because the plat layer and the parcel layer normally agree.
    // Then word-by-word, because when they disagree it is over an inserted word
    // like AMD or REPLAT — the same mismatch that made the plat search itself
    // return nothing for a plat that exists.
    const exact = await queryTracts(q, `SUBNAME = '${sanitizeSubdivisionQuery(q.subname)}'`);
    if (exact.length) return exact;
    const tokens = subdivisionTokens(q.subname);
    if (tokens.length >= 2) {
      return queryTracts(q, tokens.map((t) => `SUBNAME LIKE '%${t}%'`).join(' AND '));
    }
    return exact;
  }
  return queryTracts(q, null);
}

async function queryTracts(q: TractQuery, subnamePredicate: string | null): Promise<Tract[]> {
  const params = new URLSearchParams({
    outFields: 'APN,OWNER_NAME,PUC,LAND_SIZE,SUBNAME',
    returnGeometry: 'true',
    outSR: '4326',
    resultRecordCount: '60',
    f: 'json',
  });

  const where: string[] = [`(PUC = '${PUC_COMMON_AREA}' OR OWNER_NAME LIKE '%ASSOCIATION%' OR OWNER_NAME LIKE '%HOMEOWNERS%')`];
  if (subnamePredicate) {
    where.push(subnamePredicate);
  } else if (q.bbox) {
    const [s, w, n, e] = q.bbox;
    params.set('geometry', `${w},${s},${e},${n}`);
    params.set('geometryType', 'esriGeometryEnvelope');
    params.set('inSR', '4326');
    params.set('spatialRel', 'esriSpatialRelIntersects');
  } else {
    throw new ServiceError('Maricopa County parcels', 'guard', 'Find the property first, then look up its common areas.');
  }
  params.set('where', where.join(' AND '));

  const raw = await fetchJson('Maricopa County parcels', `${BASE}?${params.toString()}`, { timeoutMs: 25000 });
  const parsed = Schema.safeParse(raw);
  if (!parsed.success) throw new ServiceError('Maricopa County parcels', 'malformed', 'The county answered in an unexpected shape.');

  const out: Tract[] = [];
  for (const f of parsed.data.features) {
    const rings: LatLng[][] = (f.geometry?.rings ?? [])
      .filter((r) => r.length >= 3)
      .map((r) => r.map((pt) => [pt[1], pt[0]] as LatLng));
    if (!rings.length) continue;
    const owner = (f.attributes.OWNER_NAME ?? '').trim();
    const puc = (f.attributes.PUC ?? '').trim();
    // keep it only if the county says common area OR the owner reads as an association
    if (puc !== PUC_COMMON_AREA && !ASSOC.test(owner)) continue;
    out.push({
      apn: (f.attributes.APN ?? '').trim(),
      owner,
      puc,
      countySqFt: Math.round(f.attributes.LAND_SIZE ?? 0),
      measuredSqFt: Math.round(rings.reduce((s, r) => s + polygonAreaSqFt(r), 0)),
      subname: f.attributes.SUBNAME ?? undefined,
      rings,
    });
  }
  return out.sort((a, b) => b.measuredSqFt - a.measuredSqFt);
}

/** group tracts by owner so the user can see whose ground is whose at a glance */
export function groupByOwner(tracts: Tract[]): { owner: string; tracts: Tract[]; totalSqFt: number }[] {
  const by = new Map<string, Tract[]>();
  for (const t of tracts) {
    const k = t.owner || 'Owner not listed';
    by.set(k, [...(by.get(k) ?? []), t]);
  }
  return [...by.entries()]
    .map(([owner, list]) => ({ owner, tracts: list, totalSqFt: list.reduce((s, t) => s + t.measuredSqFt, 0) }))
    .sort((a, b) => b.totalSqFt - a.totalSqFt);
}
